import type { Room } from "@colyseus/sdk"
import Phaser, { GameObjects, Scene } from "phaser"
import {
  BERRY_TREE_POSITIONS,
  BOARD_WIDTH,
  getRegionTint,
  RegionDetails
} from "../../../../config"
import type { DesignTiled } from "../../../../core/design"
import { FLOWER_POTS_POSITIONS_BLUE } from "../../../../core/flower-pots"
import { canSell } from "../../../../core/pokemon-entity"
import type Player from "../../../../models/colyseus-models/player"
import { PokemonClasses } from "../../../../models/colyseus-models/pokemon"
import type GameState from "../../../../rooms/states/game-state"
import {
  type IDragDropCombineMessage,
  type IDragDropItemMessage,
  type IDragDropMessage,
  Transfer
} from "../../../../types"
import { DungeonMusic, type DungeonPMDO } from "../../../../types/enum/Dungeon"
import { GamePhaseState } from "../../../../types/enum/Game"
import { Gifts } from "../../../../types/enum/GiftShop"
import { type Item, ItemRecipe, Mulches } from "../../../../types/enum/Item"
import type { Pkm } from "../../../../types/enum/Pokemon"
import { isIn } from "../../../../utils/array"
import { throttle } from "../../../../utils/function"
import { logger } from "../../../../utils/logger"
import { clamp } from "../../../../utils/number"
import { schemaValues } from "../../../../utils/schemas"
import { clearTitleNotificationIcon } from "../../../../utils/window"
import { cyclePlayers, playerClick } from "../../pages/game"
import { playMusic, playSound, SOUNDS } from "../../pages/utils/audio"
import { transformBoardCoordinates } from "../../pages/utils/utils"
import { preference, savePreferences } from "../../preferences"
import AnimationManager from "../animation-manager"
import { clearAbilityAnimations } from "../components/abilities-animations"
import BattleManager from "../components/battle-manager"
import BoardManager, { BoardMode } from "../components/board-manager"
import ItemContainer from "../components/item-container"
import ItemsContainer from "../components/items-container"
import LoadingManager from "../components/loading-manager"
import MinigameManager from "../components/minigame-manager"
import PokemonSprite, { resetSpriteCounts } from "../components/pokemon"
import { SellZone } from "../components/sell-zone"
import { UseItemZone } from "../components/useitem-zone"
import WanderersManager from "../components/wanderers-manager"
import WeatherManager from "../components/weather-manager"
import { DEPTH } from "../depths"

export default class GameScene extends Scene {
  tilemaps: Map<DungeonPMDO, DesignTiled> = new Map<DungeonPMDO, DesignTiled>()
  room: Room<GameState> | undefined
  uid: string | undefined
  mapName: DungeonPMDO | "town" = "town"
  map: Phaser.Tilemaps.Tilemap | undefined
  battleGroup: GameObjects.Group | undefined
  abilitiesVfxGroup: GameObjects.Group | undefined
  animationManager: AnimationManager | undefined
  itemsContainer: ItemsContainer | undefined
  board: BoardManager | undefined
  battle: BattleManager | undefined
  weatherManager: WeatherManager | undefined
  wandererManager?: WanderersManager
  music: Phaser.Sound.WebAudioSound | undefined
  pokemonHovered: PokemonSprite | null = null
  pokemonDragged: PokemonSprite | null = null
  shopIndexHovered: number | null = null
  itemDragged: ItemContainer | null = null
  dropSpots: Phaser.GameObjects.Image[] = []
  sellZone: SellZone | undefined
  useItemZone: UseItemZone | undefined
  lastDragDropPokemon: PokemonSprite | undefined
  lastPokemonDetail: PokemonSprite | null = null
  minigameManager: MinigameManager | null = null
  loadingManager: LoadingManager | null = null
  started: boolean = false
  spectate: boolean = false
  spectatedPlayerId: string | undefined = undefined

  constructor() {
    super({
      key: "gameScene",
      active: false
    })
  }

  init(data: {
    room: Room<GameState>
    uid: string
    spectate: boolean
    spectatedPlayerId?: string
  }) {
    this.tilemaps = new Map()
    this.room = data.room
    this.spectate = data.spectate
    this.spectatedPlayerId = data.spectatedPlayerId
    this.uid = data.uid
    this.started = false
    globalThis.devcommand = (action: string, ...params: any[]) =>
      this.room?.send(Transfer.DEV, { action, ...params })
  }

  preload() {
    resetSpriteCounts()
    this.loadingManager = new LoadingManager(this)

    this.load.on("progress", (value: number) => {
      this.room?.send(Transfer.LOADING_PROGRESS, value * 100)
    })

    this.loadingManager!.preloadingPromise.catch((err) =>
      logger.error("loading error", err)
    ).then(() => {
      logger.debug("Loading complete")
      if (!this.started) {
        this.room?.send(Transfer.LOADING_COMPLETE)
      }
    })

    this.room!.onMessage(Transfer.LOADING_COMPLETE, () => {
      if (!this.started) {
        logger.debug("Game starting")
        this.started = true
        this.startGame()
      }
    })
  }

  startGame() {
    if (this.uid && this.room) {
      this.registerKeys()
      this.setupCamera()
      this.input.dragDistanceThreshold = 1

      const playerUids = schemaValues(this.room.state.players).map((p) => p.id)
      const player = this.room.state.players.get(
        this.spectate ? (this.spectatedPlayerId ?? playerUids[0]) : this.uid
      ) as Player

      this.setMap(player.map)
      this.setupMouseEvents()
      this.battleGroup = this.add.group()
      this.abilitiesVfxGroup = this.add.group()
      this.animationManager = new AnimationManager(this)
      this.minigameManager = new MinigameManager(
        this,
        this.animationManager,
        this.uid,
        this.room.state
      )

      this.itemsContainer = new ItemsContainer(
        this,
        player.items,
        22 * 24 + 10,
        5 * 24 + 10,
        null,
        this.uid
      )
      this.board = new BoardManager(
        this,
        player,
        this.animationManager,
        this.uid,
        this.room.state
      )
      this.battle = new BattleManager(
        this,
        this.battleGroup,
        this.room.state.simulations.get(player.simulationId),
        this.animationManager,
        player
      )

      this.weatherManager = new WeatherManager(this)
      this.weatherManager?.setTownDaytime(0)

      this.wandererManager = new WanderersManager(this)
      if (!this.music) {
        playMusic(
          this,
          RegionDetails[player.map].music ?? DungeonMusic.TREASURE_TOWN
        )
      }
      clearTitleNotificationIcon()
    }
  }

  toggleTilesetAnimation(paused: boolean) {
    if (!this.map) return
    this.map.layers.forEach((layer) => {
      layer.tilemapLayer.setTimerPaused(paused)
    })
  }

  update(time: number, delta: number) {
    super.update(time, delta)
    if (this.lastPokemonDetail) {
      this.lastPokemonDetail.updateTooltipPosition()
    }
    if (
      this.room?.state?.phase === GamePhaseState.TOWN &&
      this.minigameManager
    ) {
      this.minigameManager.update()
    }
  }

  setupCamera() {
    this.cameras.main.setBounds(
      0,
      0,
      (this.map?.widthInPixels ?? 1200) * 2,
      (this.map?.heightInPixels ?? 768) * 2
    )

    this.input.on("wheel", (pointer, gameObjects, deltaX, deltaY, deltaZ) => {
      if (preference("cameraLocked")) return
      this.cameras.main.zoom = clamp(
        this.cameras.main.zoom - Math.sign(deltaY) * 0.1,
        1,
        2
      )
      //this.cameras.main.centerOn(pointer.worldX, pointer.worldY)
      if (deltaY < 0) {
        this.cameras.main.pan(pointer.worldX, pointer.worldY, 400, "Power2")
      } else if (this.cameras.main.zoom === 1) {
        this.cameras.main.pan(0, 0, 400, "Power2")
      }
    })

    this.input.on("pointermove", (pointer) => {
      if (!pointer.isDown || this.itemDragged || this.pokemonDragged) return
      const cam = this.cameras.main
      if (cam.zoom === 1 || preference("cameraLocked")) return
      cam.scrollX -= (pointer.x - pointer.prevPosition.x) / cam.zoom
      cam.scrollY -= (pointer.y - pointer.prevPosition.y) / cam.zoom
    })
  }

  registerKeys() {
    const keybindings = preference("keybindings")

    this.input.keyboard!.removeAllListeners()

    if (!this.spectate) {
      this.input.keyboard!.on(
        "keydown-" + keybindings.refresh,
        throttle(() => {
          playSound(SOUNDS.REFRESH, 0.5)
          this.refreshShop()
        }, 300)
      )

      this.input.keyboard!.on("keydown-" + keybindings.lock, () => {
        this.room?.send(Transfer.LOCK)
      })

      this.input.keyboard!.on("keydown-" + keybindings.buy_xp, () => {
        this.buyExperience()
      })

      this.input.keyboard!.on("keydown-" + keybindings.sell, (e) => {
        if (this.pokemonDragged != null) return
        if (this.shopIndexHovered !== null) {
          this.removeFromShop(this.shopIndexHovered)
          this.shopIndexHovered = null
        } else if (
          this.pokemonHovered &&
          this.pokemonHovered
            .getBounds()
            .contains(
              this.input.activePointer.worldX,
              this.input.activePointer.worldY
            )
        ) {
          this.sellPokemon(this.pokemonHovered)
          this.pokemonHovered = null
        }
      })

      this.input.keyboard!.on("keydown-" + keybindings.switch, () => {
        if (this.pokemonHovered) {
          this.switchBetweenBenchAndBoard(this.pokemonHovered)
        }
      })

      this.input.keyboard!.on("keydown-" + keybindings.board_return, () => {
        playerClick(this.uid!)
      })
    }

    this.input.keyboard!.on("keydown-" + keybindings.camera_lock, () => {
      savePreferences({ cameraLocked: !preference("cameraLocked") })
    })

    this.input.keyboard!.on("keydown-" + keybindings.prev_player, () => {
      cyclePlayers(-1)
    })

    this.input.keyboard!.on("keydown-" + keybindings.next_player, () => {
      cyclePlayers(1)
    })
  }

  refreshShop() {
    const player = this.room?.state.players.get(this.uid!)
    const rollCost = (player?.shopFreeRolls ?? 0) > 0 ? 0 : 1
    const canRoll = (player?.money ?? 0) >= rollCost
    if (player && player.alive && canRoll && player === this.board?.player) {
      this.room?.send(Transfer.REFRESH)
      playSound(SOUNDS.REFRESH, 0.5)
    }
  }

  buyExperience() {
    this.room?.send(Transfer.LEVEL_UP)
  }

  sellPokemon(pokemon: PokemonSprite) {
    if (!pokemon) return
    this.room?.send(Transfer.SELL_POKEMON, pokemon.id)
  }

  useitem(item: Item) {
    if (!item) return
    this.room?.send(Transfer.USE_ITEM, item)
  }

  removeFromShop(index: number) {
    this.room?.send(Transfer.REMOVE_FROM_SHOP, index)
  }

  switchBetweenBenchAndBoard(pokemon: PokemonSprite) {
    if (!pokemon) return
    this.room?.send(Transfer.SWITCH_BENCH_AND_BOARD, pokemon.id)
  }

  updatePhase(newPhase: GamePhaseState, previousPhase: GamePhaseState) {
    this.weatherManager?.clearWeather()
    clearAbilityAnimations(this)
    this.resetDragState()

    if (previousPhase === GamePhaseState.TOWN) {
      this.minigameManager?.dispose()
    }

    if (newPhase === GamePhaseState.FIGHT) {
      this.board?.battleMode(true)
    } else if (newPhase === GamePhaseState.TOWN) {
      this.board?.minigameMode()
      this.weatherManager?.setTownDaytime(this.room?.state.stageLevel ?? 0)
    } else {
      this.board?.pickMode(true)
      if (previousPhase === GamePhaseState.TOWN) {
        this.time.delayedCall(0, () => {
          const board = this.board
          if (!board || board.mode !== BoardMode.PICK) return
          board.renderBerryTrees()
          board.renderFlowerPots()
          board.renderGroundHoles()
          board.renderTrainingBag()
        })
      }
    }
  }

  preloadMaps(mapNames: DungeonPMDO[]) {
    return Promise.all(
      mapNames.map((mapName: DungeonPMDO) =>
        fetch(`/tilemap/${mapName}`)
          .then((res) => res.json())
          .then((tilemap: DesignTiled) => {
            this.tilemaps.set(mapName, tilemap)
            tilemap.tilesets.forEach((t) => {
              //logger.debug(`loading tileset ${mapName + "/" + t.name}`)
              this.load.image(
                mapName + "/" + t.name,
                "/assets/tilesets/" + mapName + "/" + t.image
              )
            })
            this.load.tilemapTiledJSON("map_" + mapName, tilemap)
          })
      )
    )
  }

  async setMap(mapName: DungeonPMDO | "town") {
    this.board?.hideGroundHoles()
    this.mapName = mapName

    if (mapName === "town") {
      this.map = this.add.tilemap("town")
      const tileset = this.map.addTilesetImage("town_tileset", "town_tileset")!
      this.map.createLayer("layer0", tileset, 0, 0)?.setScale(2, 2)
      this.map.createLayer("layer1", tileset, 0, 0)?.setScale(2, 2)
      this.map.createLayer("layer2", tileset, 0, 0)?.setScale(2, 2)
      return
    }

    const tilemap = this.tilemaps.get(mapName)
    if (!tilemap)
      return logger.error(`Tilemap not yet loaded for map ${mapName}`)

    const map = this.make.tilemap({ key: "map_" + mapName })
    if (this.map) this.map.destroy()
    this.map = map
    tilemap.layers.forEach((layer) => {
      const tileset = map.addTilesetImage(
        layer.name,
        mapName + "/" + layer.name
      )!
      tileset.image?.setFilter(Phaser.Textures.FilterMode.NEAREST)
      map.createLayer(layer.name, tileset, 0, 0)?.setScale(2, 2)
    })
    this.toggleTilesetAnimation(preference("disableAnimatedTilemap"))

    // update region tint on pokemons
    this.board?.pokemons.forEach((p) => {
      p.sprite.setTint(
        getRegionTint(this.mapName, preference("colorblindMode"))
      )
    })
    this.battle?.pokemonSprites.forEach((p) => {
      p.sprite.setTint(
        getRegionTint(this.mapName, preference("colorblindMode"))
      )
    })
  }

  resetDragState() {
    if (this.pokemonDragged) {
      this.input.emit(
        "dragend",
        this.input.activePointer,
        this.pokemonDragged,
        false
      )
      this.pokemonDragged = null
    } else if (this.itemDragged) {
      this.itemDragged.closeDetail()
      if (this.itemDragged.input) {
        this.itemDragged.x = this.itemDragged.input.dragStartX
        this.itemDragged.y = this.itemDragged.input.dragStartY
      }
      this.input.emit("dragend", this.input.pointer1, this.itemDragged, false)
      this.itemDragged = null
    }
    this.input.setDragState(this.input.pointer1, 0)
  }

  setupMouseEvents() {
    this.sellZone = new SellZone(this)
    this.useItemZone = new UseItemZone(this)
    this.dropSpots = []

    for (let y = 0; y < 4; y++) {
      for (let x = 0; x < 8; x++) {
        const coord = transformBoardCoordinates(x, y)
        const zone = this.add.zone(coord[0], coord[1], 96, 96)
        zone.setRectangleDropZone(96, 96)
        zone.setName("board-zone")
        const spotSprite = this.add
          .image(zone.x, zone.y, "board_cell", 0)
          .setVisible(false)
          .setData({ x, y })
          .setDepth(DEPTH.DROP_CELL)
          .setScale(2, 2)
        zone.setData({ x, y, sprite: spotSprite })
        this.dropSpots.push(spotSprite)
      }
    }

    for (let i = 0; i < FLOWER_POTS_POSITIONS_BLUE.length; i++) {
      const [x, y] = FLOWER_POTS_POSITIONS_BLUE[i]
      const zone = this.add.zone(x, y, 48, 48)
      zone.setRectangleDropZone(48, 48)
      zone.setName("flower-pot-zone")
      zone.setData({ x, y, index: i })
    }

    for (let i = 0; i < BERRY_TREE_POSITIONS.length; i++) {
      const [x, y] = BERRY_TREE_POSITIONS[i]
      const zone = this.add.zone(x, y, 48, 48)
      zone.setRectangleDropZone(48, 48)
      zone.setName("berry-tree-zone")
      zone.setData({ x, y, index: i })
    }

    this.input.on("pointerdown", (pointer) => {
      if (
        pointer.leftButtonDown() &&
        this.minigameManager &&
        this.room?.state.phase === GamePhaseState.TOWN &&
        !this.spectate
      ) {
        // compute actual x/y coordinates after taking into account camera scroll and zoom
        const camera = this.cameras.main
        const x = camera.worldView.left + pointer.x / camera.zoom
        const y = camera.worldView.top + pointer.y / camera.zoom
        const [minX, maxY] = transformBoardCoordinates(-1, 0)
        const [maxX, minY] = transformBoardCoordinates(8, 7)
        if (x < minX || x > maxX || y > maxY || y < minY) return
        const vector = this.minigameManager.getVector(x, y)
        this.room?.send(Transfer.VECTOR, vector)

        const clickAnimation = this.add.sprite(
          x,
          y,
          "attacks",
          `WATER/cell/000.png`
        )
        clickAnimation.setDepth(DEPTH.INDICATOR)
        clickAnimation.anims.play("WATER/cell")
        this.tweens.add({
          targets: clickAnimation,
          x,
          y,
          ease: "linear",
          yoyo: true,
          duration: 200,
          onComplete: () => {
            clickAnimation.destroy()
          }
        })
      }
      if (!preference("showDetailsOnDoubleClick")) {
        if (this.board && !pointer.rightButtonDown()) {
          this.board.closeTooltips()
        }
      }
    })

    this.input.on(
      Phaser.Input.Events.GAMEOBJECT_OVER,
      (pointer, gameObject: Phaser.GameObjects.GameObject) => {
        if (gameObject instanceof PokemonSprite && gameObject.draggable) {
          this.setPokemonHovered(gameObject)
        }
      }
    )

    this.input.on(
      Phaser.Input.Events.GAMEOBJECT_OUT,
      (pointer, gameObject: Phaser.GameObjects.GameObject) => {
        if (this.pokemonHovered === gameObject) {
          this.clearHovered(this.pokemonHovered.sprite)
          this.pokemonHovered = null
        }
      }
    )

    this.input.on(
      "dragstart",
      (pointer, gameObject: Phaser.GameObjects.GameObject) => {
        if (gameObject instanceof PokemonSprite) {
          this.pokemonDragged = gameObject
          this.pokemonDragged.setDepth(DEPTH.DRAGGED_POKEMON)
          this.dropSpots.forEach((spot) => {
            if (
              this.room?.state.phase === GamePhaseState.PICK ||
              spot.getData("y") === 0
            ) {
              spot.setFrame(0).setVisible(true)
            }
          })

          if (
            this.sellZone &&
            canSell(
              this.pokemonDragged.name as Pkm,
              this.room?.state.specialGameRule,
              this.room?.state.specialGameRule2
            )
          ) {
            this.sellZone.showForPokemon(this.pokemonDragged)
          }
        } else if (gameObject instanceof ItemContainer) {
          this.itemDragged = gameObject
          if (this.useItemZone && isIn(Gifts, this.itemDragged.name)) {
            this.useItemZone.showForItem(this.itemDragged.name)
          }
        }
      }
    )

    this.input.on(
      "drag",
      (
        pointer,
        gameObject: Phaser.GameObjects.GameObject,
        dragX: number,
        dragY: number
      ) => {
        const g = <Phaser.GameObjects.Container>gameObject
        g.x = dragX
        g.y = dragY
        if (g && this.pokemonDragged != null) {
          const pkm = <Pkm>this.pokemonDragged!.name
          const pokemon = new PokemonClasses[pkm](pkm)

          this.dropSpots.forEach((spot) => {
            const inBench = spot.getData("y") === 0
            let visible = false
            if (inBench) {
              visible = pokemon.canBeBenched
            } else if (this.room?.state.phase === GamePhaseState.PICK) {
              visible = true
            }
            spot.setVisible(visible)
          })
          if (
            this.sellZone?.visible === false &&
            canSell(
              this.pokemonDragged.name as Pkm,
              this.room?.state.specialGameRule,
              this.room?.state.specialGameRule2
            )
          ) {
            this.sellZone.setVisible(true)
          }
        }
      }
    )

    this.input.on(
      "drop",
      (
        pointer,
        gameObject: Phaser.GameObjects.GameObject,
        dropZone: Phaser.GameObjects.Zone
      ) => {
        this.dropSpots.forEach((spot) => spot.setVisible(false))
        this.sellZone?.hide()
        this.useItemZone?.hide()

        if (gameObject instanceof PokemonSprite) {
          // POKEMON -> BOARD-ZONE = PLACE POKEMON
          if (dropZone.name == "board-zone") {
            const [x, y] = [dropZone.getData("x"), dropZone.getData("y")]
            if (gameObject.positionX !== x || gameObject.positionY !== y) {
              this.dispatchEvent<IDragDropMessage>(Transfer.DRAG_DROP, {
                x,
                y,
                id: gameObject.id
              })
              this.lastDragDropPokemon = gameObject
            } else {
              // RETURN TO ORIGINAL SPOT
              gameObject.setPosition(...transformBoardCoordinates(x, y))
            }
          }
          // POKEMON -> SELL-ZONE = SELL POKEMON
          else if (dropZone.name == "sell-zone") {
            if (gameObject === this.pokemonDragged) {
              this.sellPokemon(this.pokemonDragged)
            }
          }
          // RETURN TO ORIGINAL SPOT
          else {
            const [x, y] = transformBoardCoordinates(
              gameObject.positionX,
              gameObject.positionY
            )
            gameObject.setPosition(x, y)
          }
          gameObject.setDepth(DEPTH.POKEMON)
          this.pokemonDragged = null
        } else if (
          gameObject instanceof ItemContainer &&
          this.itemDragged != null
        ) {
          // Item -> Item = COMBINE
          if (dropZone instanceof ItemContainer) {
            this.dispatchEvent<IDragDropCombineMessage>(
              Transfer.DRAG_DROP_COMBINE,
              {
                itemA: dropZone.name,
                itemB: gameObject.name
              }
            )
          }
          // Item -> POKEMON(board zone) = EQUIP
          else if (
            dropZone.name === "board-zone" &&
            (this.room?.state.phase == GamePhaseState.PICK ||
              dropZone.getData("y") == 0)
          ) {
            this.dispatchEvent<IDragDropItemMessage>(Transfer.DRAG_DROP_ITEM, {
              zone: dropZone.name,
              index:
                dropZone.getData("x") + dropZone.getData("y") * BOARD_WIDTH,
              id: gameObject.name
            })
          }
          // Item -> POKEMON(flower pot zone) = EQUIP OR MULCH
          else if (dropZone.name === "flower-pot-zone") {
            this.dispatchEvent<IDragDropItemMessage>(Transfer.DRAG_DROP_ITEM, {
              zone: dropZone.name,
              index: dropZone.getData("index"),
              id: gameObject.name
            })
          }
          // Item -> berry tree zone = MULCH
          else if (dropZone.name === "berry-tree-zone") {
            this.dispatchEvent<IDragDropItemMessage>(Transfer.DRAG_DROP_ITEM, {
              zone: dropZone.name,
              index: dropZone.getData("index"),
              id: gameObject.name
            })
          }
          // Use Item zone
          else if (dropZone.name == "useitem-zone") {
            this.useitem(this.itemDragged.name)
          }
          // RETURN TO ORIGINAL SPOT
          else {
            const player = this.room?.state.players.get(this.uid!)
            if (player) this.itemsContainer?.render(player.items)
          }
          this.itemDragged = null
        }
      },
      this
    )

    this.input.on("dragend", (pointer, gameObject, dropped) => {
      this.sellZone?.hide()
      this.useItemZone?.hide()
      this.dropSpots.forEach((spot) => spot.setVisible(false))
      if (!dropped && gameObject?.input) {
        gameObject.x = gameObject.input.dragStartX
        gameObject.y = gameObject.input.dragStartY
      }
      this.pokemonDragged = null
      this.itemDragged = null
    })

    this.input.on(
      "dragenter",
      (pointer, gameObject, dropZone) => {
        if (
          gameObject instanceof ItemContainer &&
          dropZone instanceof ItemContainer
        ) {
          // item dragged above another item: find the resulting item
          for (const [key, value] of Object.entries(ItemRecipe)) {
            if (
              (value[0] == gameObject.name && value[1] == dropZone.name) ||
              (value[0] == dropZone.name && value[1] == gameObject.name)
            ) {
              this.itemsContainer?.sendToBack(dropZone)
              gameObject.showTempDetail(key as Item)
              break
            }
          }
        }

        if (
          dropZone.name === "board-zone" &&
          gameObject instanceof PokemonSprite
        ) {
          // pokemon dragged above board zone: highlight the cell
          dropZone.getData("sprite")?.setFrame(1)
        }

        if (
          gameObject instanceof ItemContainer &&
          dropZone.name === "board-zone" &&
          !(
            this.room?.state.phase == GamePhaseState.FIGHT &&
            dropZone.getData("y") != 0
          ) &&
          this.board?.pokemons
        ) {
          const pokemonOnCell = [...this.board.pokemons.values()].find(
            (p) =>
              p.positionX === dropZone.getData("x") &&
              p.positionY === dropZone.getData("y")
          )
          if (pokemonOnCell) {
            // item dragged over a pokemon, highlight the pokemon
            this.setPokemonHovered(pokemonOnCell)
          }
        }

        if (
          gameObject instanceof ItemContainer &&
          dropZone.name === "flower-pot-zone" &&
          isIn(Mulches, gameObject.name)
        ) {
          const flowerMonSprite =
            this.board?.flowerPokemonsInPots[dropZone.getData("index")]
          if (flowerMonSprite) {
            this.setPokemonHovered(flowerMonSprite)
          }
        }

        if (
          gameObject instanceof ItemContainer &&
          dropZone.name === "berry-tree-zone" &&
          isIn(Mulches, gameObject.name)
        ) {
          const berryTree = this.board?.berryTrees[dropZone.getData("index")]
          if (berryTree) {
            this.setHovered(berryTree.sprite)
          }
        }

        if (
          dropZone.name === "sell-zone" &&
          gameObject instanceof PokemonSprite
        ) {
          // pokemon dragged above sell zone: highlight the sell zone
          this.sellZone?.onDragEnter()
        }

        if (
          dropZone.name === "useitem-zone" &&
          gameObject instanceof ItemContainer
        ) {
          // pokemon dragged above sell zone: highlight the sell zone
          this.useItemZone?.onDragEnter()
        }
      },
      this
    )

    this.input.on(
      "dragleave",
      (pointer, gameObject, dropZone) => {
        if (
          gameObject instanceof ItemContainer &&
          dropZone instanceof ItemContainer
        ) {
          gameObject.closeDetail()
        }

        if (
          dropZone.name === "board-zone" &&
          gameObject instanceof PokemonSprite
        ) {
          dropZone.getData("sprite")?.setFrame(0)
        }

        if (
          dropZone.name === "sell-zone" &&
          gameObject instanceof PokemonSprite
        ) {
          this.sellZone?.onDragLeave()
        }

        if (
          dropZone.name === "useitem-zone" &&
          gameObject instanceof ItemContainer
        ) {
          this.useItemZone?.onDragLeave()
        }

        if (
          dropZone.name === "board-zone" &&
          gameObject instanceof ItemContainer &&
          this.board?.pokemons
        ) {
          const pokemonOnCell = [...this.board.pokemons.values()].find(
            (p) =>
              p.positionX === dropZone.getData("x") &&
              p.positionY === dropZone.getData("y")
          )
          if (pokemonOnCell) {
            this.clearHovered(pokemonOnCell.sprite)
            if (this.pokemonHovered === pokemonOnCell) {
              this.pokemonHovered = null
            }
          }
        }

        if (
          dropZone.name === "flower-pot-zone" &&
          gameObject instanceof ItemContainer &&
          isIn(Mulches, gameObject.name)
        ) {
          const flowerPot =
            this.board?.flowerPokemonsInPots[dropZone.getData("index")]
          if (flowerPot) {
            this.clearHovered(flowerPot.sprite)
            if (this.pokemonHovered === flowerPot) {
              this.pokemonHovered = null
            }
          }
        }

        if (
          dropZone.name === "berry-tree-zone" &&
          gameObject instanceof ItemContainer &&
          isIn(Mulches, gameObject.name)
        ) {
          const berryTree = this.board?.berryTrees[dropZone.getData("index")]
          if (berryTree) {
            this.clearHovered(berryTree.sprite)
          }
        }
      },
      this
    )
  }

  setPokemonHovered(pokemonSprite: PokemonSprite) {
    if (this.pokemonHovered != null) {
      this.clearHovered(this.pokemonHovered.sprite)
    }
    this.pokemonHovered = pokemonSprite
    const thickness = Math.round(
      1 + Math.log(pokemonSprite.pokemon.def + pokemonSprite.pokemon.speDef)
    )
    this.setHovered(pokemonSprite.sprite, thickness)
  }

  setHovered(sprite: Phaser.GameObjects.Sprite, thickness = 2) {
    if (this.game.renderer.type !== Phaser.WEBGL) return // outline plugin doesnt work with canvas renderer

    sprite.enableFilters()
    const existingOutline = sprite.getData("rexOutlineController") as
      | Phaser.Filters.Controller
      | undefined
    existingOutline?.destroy()

    const outline = sprite.filters!.internal.addRexOutline({
      thickness,
      outlineColor: 0xffffff
    })
    sprite.setData("rexOutlineController", outline)
  }

  clearHovered(sprite: Phaser.GameObjects.Sprite) {
    const outline = sprite.getData("rexOutlineController") as
      | Phaser.Filters.Controller
      | undefined
    outline?.destroy()
    sprite.setData("rexOutlineController", null)
  }

  closeTooltips() {
    this.board?.closeTooltips()
    this.battle?.closeTooltips()
    this.minigameManager?.closeTooltips()
    this.itemsContainer?.closeTooltips()
  }

  displayMoneyGain(x: number, y: number, gain: number) {
    const textStyle = {
      fontSize: "25px",
      fontFamily: "Verdana",
      color: "#FFFF00",
      align: "center",
      strokeThickness: 2,
      stroke: "#000"
    }
    const text = this.add.existing(
      new GameObjects.Text(
        this,
        x - 40,
        y - 50,
        `${gain > 0 ? "+ " : ""}${gain} GOLD`,
        textStyle
      )
    )
    text.setDepth(DEPTH.TEXT_MAJOR)
    this.add.tween({
      targets: [text],
      ease: "Linear",
      duration: 1000,
      delay: 0,
      alpha: {
        getStart: () => 1,
        getEnd: () => 0
      },
      y: {
        getStart: () => y - 50,
        getEnd: () => y - 110
      },
      onComplete: () => {
        text.destroy()
      }
    })
  }

  shakeCamera(options?: { intensity?: number; duration?: number }) {
    if (preference("disableCameraShake")) return
    this.cameras.main.shake(
      options?.duration ?? 250,
      options?.intensity ?? 0.01
    )
  }

  dispatchEvent<T>(eventName: string, detail: T) {
    document.getElementById("game")?.dispatchEvent(
      new CustomEvent<T>(eventName, {
        detail
      })
    )
  }
}
